import React, { useMemo, useState } from 'react';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import { ShieldAlert } from 'lucide-react';
import type { SignatureBypassReason } from '../../services/api/types.gen';
import type { SigningAttestation } from './attestations';
import type { ESignatureCredentialInput } from './esignatureCredentialApi';
import { AttestationDialog } from './AttestationDialog';
import { useSignatureBypassOptions } from './useSignatureBypassOptions';

interface BypassSignatureDialogProps {
  open: boolean;
  attestation: SigningAttestation | null;
  signerName: string | null;
  signerCredentials?: string | null;
  signerEmail?: string | null;
  isLoading?: boolean;
  onConfirm: (
    credential: ESignatureCredentialInput,
    reason: SignatureBypassReason,
    comments: string | null
  ) => void | Promise<string | null | void>;
  onCancel: () => void;
}

/**
 * Confirmation for completing a chart without its clinician signature.
 *
 * Wraps `AttestationDialog` rather than reimplementing it, so lockout,
 * first-time credential setup and password reset behave exactly as they do when
 * signing. What this adds is the part specific to a forfeiture: a required
 * reason, free text when that reason is "Other", and a warning that states the
 * consequence plainly.
 */
export const BypassSignatureDialog: React.FC<BypassSignatureDialogProps> = ({
  open,
  attestation,
  signerName,
  signerCredentials,
  signerEmail,
  isLoading = false,
  onConfirm,
  onCancel,
}) => {
  const { options } = useSignatureBypassOptions(open);
  const [reason, setReason] = useState<SignatureBypassReason | ''>('');
  const [comments, setComments] = useState('');

  const commentsRequired =
    Boolean(options) && reason === options?.reason_requiring_comments;

  const isValid = useMemo(() => {
    if (!reason) return false;
    if (commentsRequired && !comments.trim()) return false;
    return true;
  }, [reason, commentsRequired, comments]);

  const handleConfirm = async (
    credential: ESignatureCredentialInput
  ): Promise<string | null | void> => {
    // Defensive: the submit button is disabled until a reason is chosen.
    if (!reason) return 'Select a reason before continuing.';
    return await onConfirm(
      credential,
      reason,
      comments.trim() ? comments.trim() : null
    );
  };

  const handleCancel = () => {
    setReason('');
    setComments('');
    onCancel();
  };

  return (
    <AttestationDialog
      open={open}
      attestation={attestation}
      signerName={signerName}
      signerCredentials={signerCredentials}
      signerEmail={signerEmail}
      isLoading={isLoading}
      title='Bypass clinician signature'
      actionLabel='Bypass signature'
      busyLabel='Bypassing…'
      extraFieldsValid={isValid}
      warning={
        <div className='flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-3 text-foreground'>
          <ShieldAlert
            className='h-4 w-4 mt-0.5 shrink-0 text-destructive'
            aria-hidden='true'
          />
          <p className='leading-relaxed'>
            This chart completes without the clinician&apos;s signature. Your
            name, the reason, and the time stay on the record. Surveyors can see
            it.
          </p>
        </div>
      }
      extraFields={
        <div className='space-y-4'>
          <div className='space-y-1.5'>
            <Label htmlFor='bypass-reason'>
              Why can&apos;t the clinician sign?
            </Label>
            <Select
              value={reason}
              onValueChange={value => setReason(value as SignatureBypassReason)}
            >
              <SelectTrigger id='bypass-reason'>
                <SelectValue placeholder='Select a reason' />
              </SelectTrigger>
              <SelectContent>
                {(options?.reasons ?? []).map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='bypass-comments'>
              {commentsRequired ? 'Reason details' : 'Notes (optional)'}
            </Label>
            <Textarea
              id='bypass-comments'
              value={comments}
              onChange={e => setComments(e.target.value)}
              placeholder={
                commentsRequired
                  ? 'State why the clinician cannot sign.'
                  : 'Add detail if it helps the record.'
              }
              rows={3}
            />
            {commentsRequired && !comments.trim() ? (
              <p className='text-xs text-muted-foreground'>
                Required when the reason is Other.
              </p>
            ) : null}
          </div>
        </div>
      }
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );
};
